import { MergedRate, PriceAdjustment, Rate } from '~/services/AccommodationService';

import { formatMargin, formatPrice } from './formatPrice';

/** What a match did to the member price. The service omits it for a rate that had none. */
export type LuxPlusOutcome = NonNullable<PriceAdjustment['luxPlusOutcome']>;

/** Why a withdrawn member price was withdrawn. */
export type LuxPlusWithdrawReason = NonNullable<PriceAdjustment['luxPlusWithdrawReason']>;

/**
 * Checked against the copy rather than trusted: a deployed service can send a reason newer than
 * this build, and the general sentence reads better than a raw `some_new_reason`.
 */
export const getLuxPlusWithdrawReason = (adjustment: PriceAdjustment): LuxPlusWithdrawReason | undefined => {
  const reason = adjustment.luxPlusWithdrawReason;
  return reason != null && reason in LUX_PLUS_WITHDRAW_REASON_DESCRIPTIONS ? reason : undefined;
};

/**
 * The dynamic price match applied to a rate, if any.
 *
 * A matched rate won its group only because its selling price was lowered to the group's cheapest
 * out of its own margin, so its displayed price no longer explains why it beat the others.
 */
export const getPriceMatchAdjustment = (rate: Rate): PriceAdjustment | undefined =>
  rate.priceAdjustments?.find((adjustment) => adjustment.source === 'price-match');

/**
 * What the rate would have been displayed at before the match.
 *
 * Never `originalSellingPrice`: that is a *selling* price, and the two differ by the
 * property-collected portion the match holds constant, so comparing it against a display price
 * shows a gap that is really just the property fees.
 *
 * The service reports `originalDisplayPrice` directly. The fallback derives it the way this did
 * before that field existed — admin deploys independently of svc-accommodation, so during a
 * rollout it can be talking to a version that does not send it yet.
 */
const getOriginalDisplayPrice = (displayPrice: number | undefined, adjustment: PriceAdjustment): number | undefined =>
  adjustment.originalDisplayPrice ??
  (displayPrice != null && adjustment.value != null ? displayPrice - adjustment.value : undefined);

/** What the match cost this rate in margin. Always AUD, whatever currency the search ran in. */
export type MarginChange = {
  beforeAud: number;
  afterAud: number;
  beforePercent?: number;
  afterPercent?: number;
};

/**
 * The rate's margin before and after the match.
 *
 * Everything here is derived from AUD quantities, so it holds whatever currency the search ran
 * in. The margin the discount came out of is `marginAud - valueAud` (the adjustment is negative).
 * The before-percentage needs the pre-match selling price in AUD, which is not on the rate — but
 * `marginAud / margin` recovers the post-match one, and the discount moved selling and margin by
 * the same amount, so adding `valueAud` back gives the price the old margin was earned on.
 */
const getMarginChange = (
  total: { margin?: number; marginAud?: number } | undefined,
  adjustment: PriceAdjustment,
): MarginChange | undefined => {
  const afterAud = total?.marginAud;
  const { valueAud } = adjustment;
  if (afterAud == null || valueAud == null) {
    return undefined;
  }

  const beforeAud = afterAud - valueAud;
  const afterPercent = total?.margin;
  if (afterPercent == null || afterPercent === 0) {
    return { beforeAud, afterAud };
  }

  const sellingPriceAud = (afterAud / afterPercent) * 100;
  const originalSellingPriceAud = sellingPriceAud - valueAud;
  return {
    beforeAud,
    afterAud,
    afterPercent,
    ...(originalSellingPriceAud > 0 && {
      beforePercent: (beforeAud / originalSellingPriceAud) * 100,
    }),
  };
};

/**
 * The margin, in AUD, of the rate this one was matched down to beat.
 *
 * That figure is what makes a match worth making: the matched rate gives up margin to reach the
 * group's cheapest price, and the trade only pays if what is left still beats what the rate it
 * displaced would have earned. The displaced rate travels in the winner's merged-rates list, so
 * it is found there by the id the adjustment recorded rather than re-fetched.
 */
const getDisplacedMarginAud = (
  mergedRates: Array<{ rate?: Record<string, unknown> }>,
  adjustment: PriceAdjustment,
): number | undefined => {
  if (!adjustment.matchedToRateId) {
    return undefined;
  }
  const displaced = mergedRates.find((merged) => merged.rate?.id === adjustment.matchedToRateId);
  const total = displaced?.rate?.total as { marginAud?: number } | undefined;
  return total?.marginAud;
};

/**
 * Everything a surface needs to explain a match, resolved once.
 *
 * The four readings below were each derived at the call site before, which is how the same match
 * came to be worded three different ways. Anything that renders a match reads it from here.
 */
export type PriceMatchDetails = {
  adjustment: PriceAdjustment;
  /** Magnitude of the discount in the requested currency. The adjustment itself is signed. */
  discount?: number;
  discountAud?: number;
  originalSellingPrice?: number;
  originalDisplayPrice?: number;
  marginChange?: MarginChange;
  displacedMarginAud?: number;
  /** What the match earned over the rate it displaced. Negative would mean a selection bug. */
  marginUpliftAud?: number;
  luxPlus: {
    outcome?: LuxPlusOutcome;
    /** The member totals were deleted outright, so there is no current price left to render. */
    withdrawn: boolean;
    withdrawReason?: LuxPlusWithdrawReason;
    /** The pre-match member price, only where the match actually moved it. */
    originalDisplayPrice?: number;
  };
};

export const getPriceMatchDetails = (
  rate: Rate,
  options: {
    /**
     * The price the surface is displaying, for the pre-match fallback. Defaults to the rate's own
     * display price; the debug tables sum selling price and property fees themselves.
     */
    displayPrice?: number;
    /** The rates this one was picked over, needed only for the displaced-margin comparison. */
    mergedRates?: Array<MergedRate>;
  } = {},
): PriceMatchDetails | undefined => {
  const adjustment = getPriceMatchAdjustment(rate);
  if (!adjustment) {
    return undefined;
  }

  const marginChange = getMarginChange(rate.total, adjustment);
  const displacedMarginAud = getDisplacedMarginAud(options.mergedRates ?? [], adjustment);
  const { luxPlusOutcome } = adjustment;

  return {
    adjustment,
    ...(adjustment.value != null && { discount: Math.abs(adjustment.value) }),
    ...(adjustment.valueAud != null && { discountAud: Math.abs(adjustment.valueAud) }),
    originalSellingPrice: adjustment.originalSellingPrice,
    originalDisplayPrice: getOriginalDisplayPrice(options.displayPrice ?? rate.total?.displayPrice, adjustment),
    marginChange,
    displacedMarginAud,
    ...(marginChange && displacedMarginAud != null && { marginUpliftAud: marginChange.afterAud - displacedMarginAud }),
    luxPlus: {
      outcome: luxPlusOutcome,
      withdrawn: luxPlusOutcome === 'withdrawn',
      ...(luxPlusOutcome === 'withdrawn' && {
        withdrawReason: getLuxPlusWithdrawReason(adjustment),
      }),
      ...(luxPlusOutcome != null &&
        luxPlusOutcome !== 'unchanged' && { originalDisplayPrice: adjustment.originalLuxPlusDisplayPrice }),
    },
  };
};

/**
 * What the match did, in one sentence.
 *
 * `basis` picks which pre-match figure to quote: the tables and the modal show a display price,
 * the grouped-rates reason column speaks about the selling price the match was measured on.
 */
export const describePriceMatch = (
  details: PriceMatchDetails,
  currency: string,
  { basis = 'display', includeLabel = false }: { basis?: 'display' | 'selling'; includeLabel?: boolean } = {},
): string => {
  const label = includeLabel ? 'Dynamic price match: ' : '';
  const original = basis === 'selling' ? details.originalSellingPrice : details.originalDisplayPrice;
  const tail =
    "out of this rate's own margin to match the cheapest rate in its group. Cost is unchanged, so the property is paid exactly what it would have been paid anyway.";
  if (original == null || details.discount == null) {
    return `${label}Discounted ${tail}`;
  }
  return `${label}Was ${formatPrice(original, currency)}, discounted by ${formatPrice(details.discount, currency)} ${tail}`;
};

/**
 * The rule behind these carries exact cost and is never returned, so this copy is the only
 * explanation admin has. Keep each one specific enough to act on.
 */
const LUX_PLUS_WITHDRAW_REASON_DESCRIPTIONS: Record<LuxPlusWithdrawReason, string> = {
  below_margin_threshold:
    "the matched price left no margin above the supplier's sharing threshold, so there was nothing left to fund a member discount",
  below_min_discount:
    'a member discount was still available at the matched price, but under the minimum the supplier will publish one for',
  rounds_to_zero:
    "the member discount rounded away to nothing at the matched price, as the supplier's own rounding would have",
  caught_up: 'the member price is partner funded and does not move with ours, and the matched public price reached it',
  no_member_discount: 'the supplier reports no member discount to re-derive at the matched price',
  rule_incomplete:
    "the supplier's member pricing rule arrived without the fields needed to re-derive it — worth raising, this is not an expected outcome",
};

/** Undefined when the member price was not withdrawn. */
export const describeLuxPlusWithdrawal = (details: PriceMatchDetails): string | undefined => {
  if (!details.luxPlus.withdrawn) {
    return undefined;
  }
  const { withdrawReason } = details.luxPlus;
  if (!withdrawReason) {
    return 'Withdrawn — the rate ended up with no member price at the matched public price.';
  }
  return `Withdrawn — ${LUX_PLUS_WITHDRAW_REASON_DESCRIPTIONS[withdrawReason]}.`;
};

/**
 * The margin trade, formatted. Always AUD: `marginAud` does not move with the search currency.
 */
export const formatMarginChange = (
  change: MarginChange,
): {
  dollars: string;
  percent?: string;
} => ({
  dollars: `${formatPrice(change.beforeAud, 'AUD')} → ${formatPrice(change.afterAud, 'AUD')}`,
  ...(change.beforePercent != null &&
    change.afterPercent != null && {
      percent: `${formatMargin(change.beforePercent)} → ${formatMargin(change.afterPercent)}`,
    }),
});

/**
 * What the trade earned against the rate it displaced.
 *
 * Worded to carry a negative too. It should not happen — selection only swaps in a challenger
 * that earns more — so a "worse for us" reading is reporting a real bug rather than hiding one.
 */
export const describeMarginUplift = (details: PriceMatchDetails): string | undefined => {
  const { displacedMarginAud, marginUpliftAud } = details;
  if (displacedMarginAud == null || marginUpliftAud == null) {
    return undefined;
  }
  return `The rate this one displaced would have earned ${formatPrice(displacedMarginAud, 'AUD')} at this price, so the match is ${formatPrice(Math.abs(marginUpliftAud), 'AUD')} ${marginUpliftAud >= 0 ? 'better' : 'worse'} for us.`;
};
